import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AlertTriangle, TrendingUp, TrendingDown, RefreshCw, Heart, DollarSign, Activity } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";

interface ClientHealth {
  id: string;
  customer_id: string | null;
  contact_id: string | null;
  overall_score: number;
  engagement_score: number;
  payment_score: number;
  satisfaction_score: number;
  churn_risk: string;
  total_revenue: number;
  active_projects: number;
  last_interaction_date: string;
  contact?: {
    id: string;
    customers?: {
      name: string;
      email: string;
    };
  };
  customer?: {
    name: string;
    email: string;
  };
}

const getRiskColor = (risk: string) => {
  switch (risk) {
    case "low": return "text-success";
    case "medium": return "text-warning";
    case "high": return "text-destructive";
    default: return "text-muted-foreground";
  }
};

const getRiskBadge = (risk: string) => {
  switch (risk) {
    case "low": return <Badge className="bg-success">Low Risk</Badge>;
    case "medium": return <Badge className="bg-warning">Medium Risk</Badge>;
    case "high": return <Badge variant="destructive">High Risk</Badge>;
    default: return <Badge variant="outline">Unknown</Badge>;
  }
};

export const ClientHealthDashboard = () => {
  const queryClient = useQueryClient();

  const { data: healthScores = [], isLoading } = useQuery({
    queryKey: ["client-health-scores"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_health_scores")
        .select(`
          *,
          contact:crm_contacts(
            id,
            customers(name, email)
          ),
          customer:customers(name, email)
        `)
        .order("overall_score", { ascending: true });
      
      if (error) throw error;
      return data as ClientHealth[];
    },
  });

  const recalculateMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("calculate-client-health");
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Client health scores recalculated");
      queryClient.invalidateQueries({ queryKey: ["client-health-scores"] });
    },
    onError: (error) => {
      toast.error("Failed to recalculate: " + error.message);
    },
  });

  if (isLoading) {
    return <Card className="p-6">Loading client health data...</Card>;
  }

  const highRiskClients = healthScores.filter(c => c.churn_risk === "high");
  const avgScore = healthScores.reduce((sum, c) => sum + (c.overall_score || 0), 0) / healthScores.length;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Clients</p>
              <h3 className="text-2xl font-bold mt-2">{healthScores.length}</h3>
            </div>
            <div className="p-3 bg-primary/10 rounded-full">
              <Heart className="h-6 w-6 text-primary" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Health Score</p>
              <h3 className="text-2xl font-bold mt-2">{avgScore.toFixed(0)}</h3>
            </div>
            <div className="p-3 bg-success/10 rounded-full">
              <Activity className="h-6 w-6 text-success" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">High Risk</p>
              <h3 className="text-2xl font-bold mt-2">{highRiskClients.length}</h3>
            </div>
            <div className="p-3 bg-destructive/10 rounded-full">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <Button
            onClick={() => recalculateMutation.mutate()}
            disabled={recalculateMutation.isPending}
            className="w-full"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${recalculateMutation.isPending ? "animate-spin" : ""}`} />
            Recalculate All
          </Button>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-6">Client Health Scores</h3>
        <div className="space-y-4">
          {healthScores.map((client) => {
            const clientName = client.contact?.customers?.name || client.customer?.name || 'Unknown Client';
            const clientEmail = client.contact?.customers?.email || client.customer?.email || '';
            
            return (
            <div key={client.id} className="glass-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {clientName.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h4 className="font-semibold">{clientName}</h4>
                    <p className="text-sm text-muted-foreground">{clientEmail}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {getRiskBadge(client.churn_risk)}
                  <div className="text-right">
                    <p className="text-2xl font-bold">{client.overall_score}</p>
                    <p className="text-xs text-muted-foreground">Overall Score</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">Engagement</span>
                    <span className="text-sm font-semibold">{client.engagement_score}</span>
                  </div>
                  <Progress value={client.engagement_score} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">Payment</span>
                    <span className="text-sm font-semibold">{client.payment_score}</span>
                  </div>
                  <Progress value={client.payment_score} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">Satisfaction</span>
                    <span className="text-sm font-semibold">{client.satisfaction_score}</span>
                  </div>
                  <Progress value={client.satisfaction_score} className="h-2" />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <div>
                    <DollarSign className="inline h-3 w-3 mr-1" />
                    ${client.total_revenue?.toLocaleString() || 0} revenue
                  </div>
                  <div>
                    <Activity className="inline h-3 w-3 mr-1" />
                    {client.active_projects || 0} active projects
                  </div>
                </div>
                {client.last_interaction_date && (
                  <p className="text-muted-foreground">
                    Last interaction: {format(new Date(client.last_interaction_date), "MMM dd, yyyy")}
                  </p>
                )}
              </div>
            </div>
            );
          })}

          {healthScores.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No client health data available. Add clients to start tracking.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};